import { useEffect } from "react"

import type { CopilotScope } from "@productlane/lib/src/ai"
import type { IconType } from "@productlane/ui"
import { cn } from "@productlane/lib/src/cn"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import { IconButton, Shortcut, Tooltip } from "@productlane/ui"
import { AiAssistantIcon } from "@productlane/ui/src/icons/project/ai-assistant-icon"

import { useCopilotState, useIsCopilotOpen } from "@/lib/states/copilot-state"
import { useRole } from "@/lib/useRole"

// Copilot toggle for the docs and changelog editors. Mirrors the thread
// right-bar's AI button (tooltip + Cmd J) but drives the standalone
// `CopilotOverlay` via `useCopilotState`.
export const CopilotToggleButton = ({
	scope,
	icon,
	hideWhenOpen = false,
	shortcut = true,
}: {
	scope: CopilotScope
	// A page where the copilot opens as a side panel shows the panel glyph.
	icon?: IconType
	// Only there to bring a closed panel back; the panel's own close button hides it.
	hideWhenOpen?: boolean
	// Off where the page already binds Cmd J, so one press never toggles twice.
	shortcut?: boolean
}) => {
	const active = useIsCopilotOpen(scope)
	const toggle = useCopilotState((s) => s.toggle)
	const { role } = useRole()
	const canSeeAiChat = role !== "VIEWER"

	useEffect(() => {
		if (!canSeeAiChat || !shortcut) return
		const handler = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
				e.preventDefault()
				toggle(scope)
			}
		}
		window.addEventListener("keydown", handler)
		return () => window.removeEventListener("keydown", handler)
	}, [canSeeAiChat, shortcut, toggle, scope])

	if (!canSeeAiChat) return null

	const hideWhenActive =
		hideWhenOpen || scope.kind === "article" || scope.kind === "changelog"
	if (active && hideWhenActive) return null

	return (
		<Tooltip
			label={
				<span className="inline-flex items-center gap-2">
					AI Copilot
					<Shortcut>
						{ShortcutSymbols.CMD}
						{ShortcutSymbols.J}
					</Shortcut>
				</span>
			}
			side="bottom"
		>
			<IconButton
				variant="ghost"
				onClick={() => toggle(scope)}
				aria-pressed={active}
				aria-label="Toggle AI Assistant"
				Icon={icon}
				iconStrokeWidth={icon ? 2 : undefined}
				className={cn(
					active &&
						"bg-gray-200 text-primary hover:bg-gray-200 dark:bg-gray-750 dark:hover:bg-gray-750",
				)}
			>
				{!icon && <AiAssistantIcon className="h-3.5 w-3.5" />}
			</IconButton>
		</Tooltip>
	)
}
